// CompanyBackground.tsx — 3-tab audit-grade vendor risk briefing.
//
// Reviewer notes are scoped per-[vendor][sectionKey] — institutional memory
// across every request for the same vendor. The original design brief
// called for per-[requestId][sectionKey] scoping; we deliberately keep
// per-vendor so a note written about Lovable's AI policy stays useful for
// the next Lovable request instead of forcing reviewers to rewrite the
// same finding every time.
//
// Layers exposed in expanded view:
//   1. Evidence       — Gemini's extracted facts, grouped by category.
//   2. Threat Model   — Claude's threat findings referencing evidence ids.
//   3. Risk Summary   — deterministic rule-engine output (Recommendation
//                       banner + Triggered Rules + the legacy 6-section view).
//
// Each AI layer is independently retriable: if Layer 2 (Threat) fails, the
// banner appears on that tab only with a "↻ Retry this layer" button.
//
// The collapsed view + critical-flag banner are driven by the synthesized
// VendorIntelPayload so existing UI consumers (queue badge, audit fan-out)
// keep working unchanged.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useToasts } from '../lib/useToasts';
import { formatRelative } from '../lib/format';
import { INTEL_LAYER_KEYS, SECTION_KEYS } from '@air/shared';
import type {
  ComplianceStatus,
  Confidence,
  EvidenceCategory,
  EvidenceFinding,
  EvidencePayload,
  EvidenceType,
  IntelLayer,
  IntelLayerKey,
  IntelSection,
  Recommendation,
  Role,
  RulesPayload,
  SectionKey,
  Severity,
  SeverityCandidate,
  ThreatFinding,
  ThreatPayload,
  TriggeredRule,
  VendorIntelPayload,
  VendorIntelV2,
  VendorReviewerNote,
} from '@air/shared';

const SEVERITY_LABEL: Record<Severity, string> = {
  clear: 'Clear',
  caution: 'Caution',
  critical: 'Critical',
  unknown: 'Limited Data',
};

const SEVERITY_CANDIDATE_LABEL: Record<SeverityCandidate, string> = {
  clear: 'Clear',
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
  critical: 'Critical',
  unknown: 'Unknown',
};

// Map advisory SeverityCandidate → 4-value Severity for pill rendering.
function candidateToSeverity(c: SeverityCandidate): Severity {
  if (c === 'critical') return 'critical';
  if (c === 'high' || c === 'moderate') return 'caution';
  if (c === 'low' || c === 'clear') return 'clear';
  return 'unknown';
}

const SECTION_DEFS: Array<{ key: SectionKey; label: string }> = [
  { key: 'aiPolicy', label: 'AI Policy' },
  { key: 'dataPolicy', label: 'Data Policy' },
  { key: 'security', label: 'Security History' },
  { key: 'ethical', label: 'Ethical Concerns' },
  { key: 'legal', label: 'Legal Concerns' },
  { key: 'track', label: 'Vendor Track Record' },
];

const EVIDENCE_CATEGORY_LABEL: Record<EvidenceCategory, string> = {
  security: 'Security',
  compliance: 'Compliance',
  ai_policy: 'AI Policy',
  data_governance: 'Data Governance',
  operational: 'Operational',
  public_reputation: 'Public Reputation',
};

const EVIDENCE_TYPE_LABEL: Record<EvidenceType, string> = {
  verified_fact: 'Verified',
  public_claim: 'Vendor Claim',
  third_party_report: '3rd-Party Report',
  missing_evidence: 'Missing',
  unknown: 'Unknown',
};

const EVIDENCE_TYPE_CLASS: Record<EvidenceType, string> = {
  verified_fact: 'clear',
  public_claim: 'caution',
  third_party_report: 'caution',
  missing_evidence: 'critical',
  unknown: 'unknown',
};

const RECOMMENDATION_LABEL: Record<Recommendation, string> = {
  approve: 'Approve',
  approve_with_conditions: 'Approve with Conditions',
  needs_security_review: 'Needs Security Review',
  needs_legal_review: 'Needs Legal Review',
  reject: 'Reject',
  insufficient_evidence: 'Insufficient Evidence',
};

const RECOMMENDATION_SEVERITY: Record<Recommendation, Severity> = {
  approve: 'clear',
  approve_with_conditions: 'caution',
  needs_security_review: 'caution',
  needs_legal_review: 'caution',
  reject: 'critical',
  insufficient_evidence: 'unknown',
};

const COMPLIANCE_MAP: Record<ComplianceStatus, { label: string; cls: string }> = {
  aligned: { label: 'Aligned', cls: 'clear' },
  native: { label: 'Native compliant', cls: 'clear' },
  baa: { label: 'BAA available', cls: 'clear' },
  type1: { label: 'Type I', cls: 'caution' },
  type2: { label: 'Type II', cls: 'clear' },
  limited: { label: 'Limited', cls: 'caution' },
  no: { label: 'Not supported', cls: 'critical' },
  none: { label: 'None', cls: 'critical' },
  na: { label: 'N/A', cls: 'unknown' },
  unknown: { label: 'Unknown', cls: 'unknown' },
};

function rollupStatus(intel: VendorIntelPayload): Severity {
  let worst: Severity = 'clear';
  for (const key of SECTION_KEYS) {
    const s = intel[key].status;
    if (s === 'critical') return 'critical';
    if (s === 'caution') worst = 'caution';
    else if (s === 'unknown' && worst === 'clear') worst = 'unknown';
  }
  return worst;
}

interface CriticalFlag {
  section: string;
  label: string;
  value: string;
  source?: { label: string; url: string };
}

function deriveCriticalFlags(intel: VendorIntelPayload): CriticalFlag[] {
  const flags: CriticalFlag[] = [];
  const walk = (section: IntelSection, sectionLabel: string): void => {
    for (const b of section.bullets) {
      if (b.severity === 'critical') {
        flags.push({ section: sectionLabel, label: b.label, value: b.value, source: b.source });
      }
    }
  };
  walk(intel.aiPolicy, 'AI Policy');
  walk(intel.dataPolicy, 'Data Policy');
  walk(intel.security, 'Security');
  walk(intel.ethical, 'Ethical');
  walk(intel.legal, 'Legal');
  walk(intel.track, 'Track Record');
  return flags;
}

type TabKey = 'evidence' | 'threat' | 'risk';

export function CompanyBackground({
  vendor,
  vendorWebsite,
  role,
}: Readonly<{ vendor: string; vendorWebsite?: string | null; role: Role }>) {
  const qc = useQueryClient();
  const toasts = useToasts();
  const intelQuery = useQuery({
    queryKey: ['vendor-intel', vendor],
    queryFn: () => api.vendorIntel(vendor),
    enabled: !!vendor,
  });

  const regen = useMutation({
    mutationFn: (layer?: IntelLayerKey) =>
      layer
        ? api.regenerateVendorIntelLayer(vendor, layer, vendorWebsite)
        : api.regenerateVendorIntel(vendor, vendorWebsite),
    onSuccess: (bundle, layer) => {
      void qc.invalidateQueries({ queryKey: ['vendor-intel', vendor] });
      const failedLayer = (INTEL_LAYER_KEYS as readonly IntelLayerKey[]).find(
        (k) => bundle[k].status === 'failed',
      );
      if (failedLayer) {
        toasts.push(
          `Layer "${failedLayer}" failed: ${bundle[failedLayer].error ?? 'unknown error'}.`,
          'err',
        );
      } else if (layer) {
        toasts.push(`Layer "${layer}" updated.`);
      } else {
        toasts.push('Vendor intel pipeline complete.');
      }
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not regenerate.', 'err');
    },
  });

  const updateNote = useMutation({
    mutationFn: ({ section, note }: { section: SectionKey; note: string }) =>
      api.updateVendorNote(vendor, section, note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor-intel', vendor] });
    },
    onError: (err) => {
      toasts.push(err instanceof ApiError ? err.message : 'Could not save note.', 'err');
    },
  });

  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('risk');
  const [search, setSearch] = useState('');
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(() => ({
    aiPolicy: false,
    dataPolicy: false,
    security: false,
    ethical: false,
    legal: false,
    track: false,
  }));
  const [didAutoOpen, setDidAutoOpen] = useState(false);

  const bundle = intelQuery.data ?? null;
  const synthesized = bundle?.synthesized ?? null;
  const evidence = bundle?.evidence ?? null;
  const threat = bundle?.threat ?? null;
  const rules = bundle?.rules ?? null;

  const notesByKey = useMemo(() => {
    const m = new Map<SectionKey, VendorReviewerNote>();
    for (const n of bundle?.notes ?? []) m.set(n.section, n);
    return m;
  }, [bundle?.notes]);

  if (intelQuery.isLoading) {
    return (
      <div className="intel-card intel--unknown">
        <div className="intel-header">
          <div className="intel-header__left">
            <div className="intel-header__icon">
              <GemIcon />
            </div>
            <div>
              <div className="intel-header__overline">
                <span>AI-Generated</span>
                <span className="intel-header__dot" />
                <span>Company Background</span>
              </div>
              <h3 className="intel-header__title">{vendor}</h3>
              <p className="intel-header__sub">Loading…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Empty state — vendor has never been researched ──
  if (!bundle || (!synthesized && evidence?.status !== 'ready')) {
    const evFailed = evidence?.status === 'failed';
    return (
      <div className="intel-card intel--unknown">
        <div className="intel-header">
          <div className="intel-header__left">
            <div className="intel-header__icon">
              <GemIcon />
            </div>
            <div>
              <div className="intel-header__overline">
                <span>AI-Generated</span>
                <span className="intel-header__dot" />
                <span>Company Background</span>
              </div>
              <h3 className="intel-header__title">{vendor}</h3>
              <p className="intel-header__sub">
                {evFailed
                  ? `Last research run failed: ${evidence?.error ?? 'unknown error'}. Try again to retry.`
                  : 'No intel yet. Run a 4-layer research pass to populate this card.'}
              </p>
            </div>
          </div>
          <div className="intel-header__right">
            <button
              className="btn btn--primary btn--sm"
              disabled={regen.isPending}
              onClick={() => regen.mutate(undefined)}
            >
              {regen.isPending ? 'Researching…' : 'Run research'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // synthesized is non-null here because of the guard above.
  const payload: VendorIntelPayload = synthesized!;
  const overall: Severity = rules?.payload?.overallSeverity ?? rollupStatus(payload);
  const flags = deriveCriticalFlags(payload);
  const sectionsFlagged = SECTION_DEFS.filter((d) => payload[d.key].status !== 'clear').length;

  function handleExpand(): void {
    setExpanded(true);
    if (!didAutoOpen) {
      setOpenSections({
        aiPolicy: payload.aiPolicy.status === 'critical',
        dataPolicy: payload.dataPolicy.status === 'critical',
        security: payload.security.status === 'critical',
        ethical: payload.ethical.status === 'critical',
        legal: payload.legal.status === 'critical',
        track: payload.track.status === 'critical',
      });
      setDidAutoOpen(true);
    }
  }

  function toggleSection(k: SectionKey): void {
    setOpenSections((prev) => ({ ...prev, [k]: !prev[k] }));
  }
  function expandAll(): void {
    setOpenSections({ aiPolicy: true, dataPolicy: true, security: true, ethical: true, legal: true, track: true });
  }
  function collapseAll(): void {
    setOpenSections({ aiPolicy: false, dataPolicy: false, security: false, ethical: false, legal: false, track: false });
  }

  if (!expanded) {
    return (
      <div className={`intel-card intel--${overall}`}>
        <div className="intel-header">
          <div className="intel-header__left">
            <div className="intel-header__icon">
              <GemIcon />
            </div>
            <div>
              <div className="intel-header__overline">
                <span>AI-Generated</span>
                <span className="intel-header__dot" />
                <span>Company Background</span>
              </div>
              <h3 className="intel-header__title">{vendor}</h3>
              <p className="intel-header__sub">{payload.summary}</p>
            </div>
          </div>
          <div className="intel-header__right">
            <SeverityPill severity={overall} />
            {flags.length > 0 && (
              <span className="intel-flag-count">
                <span className="intel-flag-count__dot" />
                {flags.length} critical {flags.length === 1 ? 'flag' : 'flags'}
              </span>
            )}
            <button className="btn btn--primary btn--sm" onClick={handleExpand}>
              Expand details →
            </button>
          </div>
        </div>
        {anyLayerFailed(bundle) && (
          <div style={{ padding: '8px 16px 16px', fontSize: 12, color: 'var(--color-danger, #d12)' }}>
            One or more pipeline layers failed. Expand to see details and retry.
          </div>
        )}
      </div>
    );
  }

  const modelLine = [
    evidence?.model ? `evidence: ${evidence.model}` : null,
    threat?.model ? `threat: ${threat.model}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className={`intel-card intel-card--expanded intel--${overall}`}>
      <div className="intel-header">
        <div className="intel-header__left">
          <div className="intel-header__icon">
            <GemIcon />
          </div>
          <div>
            <div className="intel-header__overline">
              <span>AI-Generated</span>
              <span className="intel-header__dot" />
              <span>Company Background</span>
              {evidence?.regeneratedAt && (
                <>
                  <span className="intel-header__dot" />
                  <span>Updated {formatRelative(evidence.regeneratedAt)}</span>
                </>
              )}
              {modelLine && (
                <>
                  <span className="intel-header__dot" />
                  <span title={modelLine}>{modelLine}</span>
                </>
              )}
            </div>
            <h3 className="intel-header__title">{vendor}</h3>
            <p className="intel-header__sub">{payload.summary}</p>
          </div>
        </div>
        <div className="intel-header__right">
          <SeverityPill severity={overall} />
          <button className="btn btn--ghost btn--sm" onClick={() => setExpanded(false)}>
            Collapse ↑
          </button>
        </div>
      </div>

      <div className="intel-strip">
        <div>
          <span className="intel-strip__label">Founded</span>
          <span className="intel-strip__value">{String(payload.founded)}</span>
        </div>
        <div>
          <span className="intel-strip__label">HQ</span>
          <span className="intel-strip__value">{payload.hq}</span>
        </div>
        <div>
          <span className="intel-strip__label">Size</span>
          <span className="intel-strip__value">{payload.employees}</span>
        </div>
        <div>
          <span className="intel-strip__label">Sections flagged</span>
          <span className="intel-strip__value">{sectionsFlagged} of 6</span>
        </div>
      </div>

      {flags.length > 0 && (
        <div className="intel-flags">
          <div className="intel-flags__title">
            <span className="intel-flags__bullet" /> Critical risks detected
          </div>
          <ul className="intel-flags__list">
            {flags.map((f, i) => (
              <li key={i}>
                <span className="intel-flags__section">{f.section}</span>
                <span className="intel-flags__label">{f.label}:</span>
                <span>{f.value}</span>
                {f.source?.url && (
                  <>
                    {' '}
                    <a
                      href={f.source.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="intel-sources__link"
                      style={{ marginLeft: 6 }}
                    >
                      ↗ {f.source.label}
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tab bar */}
      <div
        role="tablist"
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--color-border, rgba(255,255,255,0.1))',
          padding: '0 16px',
          marginTop: 8,
        }}
      >
        {(
          [
            { id: 'evidence' as const, label: 'Evidence', layer: evidence },
            { id: 'threat' as const, label: 'Threat Model', layer: threat },
            { id: 'risk' as const, label: 'Risk Summary', layer: rules },
          ] satisfies Array<{ id: TabKey; label: string; layer: IntelLayer<unknown> | null }>
        ).map((t) => {
          const isActive = activeTab === t.id;
          const failed = t.layer?.status === 'failed';
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: 'inherit',
                border: 'none',
                borderBottom: isActive
                  ? '2px solid var(--color-accent, #4ea1ff)'
                  : '2px solid transparent',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                opacity: isActive ? 1 : 0.7,
                position: 'relative',
              }}
            >
              {t.label}
              {failed && (
                <span
                  style={{
                    marginLeft: 6,
                    color: 'var(--color-danger, #ff5a5a)',
                  }}
                  title="Layer failed"
                >
                  ●
                </span>
              )}
            </button>
          );
        })}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            className="btn btn--secondary btn--sm"
            disabled={regen.isPending}
            onClick={() => regen.mutate(undefined)}
            title="Re-run all 3 layers"
          >
            {regen.isPending ? 'Researching…' : '↻ Regenerate all'}
          </button>
        </div>
      </div>

      <div className="intel-controls" style={{ paddingTop: 8 }}>
        <input
          type="search"
          placeholder={`Search within ${tabSearchLabel(activeTab)}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search vendor intel"
        />
        {activeTab === 'risk' && (
          <>
            <button className="btn btn--ghost btn--sm" onClick={expandAll}>Expand all</button>
            <button className="btn btn--ghost btn--sm" onClick={collapseAll}>Collapse all</button>
          </>
        )}
      </div>

      {activeTab === 'evidence' && (
        <EvidenceTab
          layer={evidence}
          search={search}
          regenLayer={() => regen.mutate('evidence')}
          regenPending={regen.isPending}
        />
      )}
      {activeTab === 'threat' && (
        <ThreatTab
          layer={threat}
          evidenceFindings={evidence?.payload?.findings ?? []}
          search={search}
          regenLayer={() => regen.mutate('threat')}
          regenPending={regen.isPending}
          jumpToEvidence={() => setActiveTab('evidence')}
        />
      )}
      {activeTab === 'risk' && (
        <RiskTab
          rules={rules}
          payload={payload}
          openSections={openSections}
          toggleSection={toggleSection}
          notesByKey={notesByKey}
          onSaveNote={(section, text) => updateNote.mutate({ section, note: text })}
          role={role}
          saving={updateNote.isPending}
          savingSection={updateNote.variables?.section}
          search={search}
        />
      )}

      <p className="intel-disclaimer">
        Background is AI-generated from public sources. Layer 1 extracts evidence (Gemini default),
        Layer 2 models threat (Claude default), Layer 3 is a deterministic rule engine. Verify
        before relying on it for a decision.
      </p>
    </div>
  );
}

function tabSearchLabel(t: TabKey): string {
  if (t === 'evidence') return 'evidence';
  if (t === 'threat') return 'threat findings';
  return 'risk summary';
}

function anyLayerFailed(b: VendorIntelV2): boolean {
  return (INTEL_LAYER_KEYS as readonly IntelLayerKey[]).some((k) => b[k].status === 'failed');
}

// ── Evidence tab ────────────────────────────────────────────────────────────

function EvidenceTab({
  layer,
  search,
  regenLayer,
  regenPending,
}: Readonly<{
  layer: IntelLayer<EvidencePayload> | null;
  search: string;
  regenLayer: () => void;
  regenPending: boolean;
}>) {
  if (!layer || layer.status === 'pending' || !layer.payload) {
    if (layer?.status === 'failed') {
      return (
        <LayerFailureBanner
          error={layer.error}
          layerLabel="Evidence (Layer 1)"
          onRetry={regenLayer}
          pending={regenPending}
        />
      );
    }
    return <EmptyLayerNote text="Evidence layer hasn't run yet." onRun={regenLayer} pending={regenPending} />;
  }
  const ev = layer.payload;
  const q = search.trim().toLowerCase();
  const matches = (f: EvidenceFinding): boolean =>
    !q ||
    f.finding.toLowerCase().includes(q) ||
    EVIDENCE_CATEGORY_LABEL[f.category].toLowerCase().includes(q);

  const grouped = new Map<EvidenceCategory, EvidenceFinding[]>();
  for (const f of ev.findings) {
    if (!matches(f)) continue;
    const arr = grouped.get(f.category) ?? [];
    arr.push(f);
    grouped.set(f.category, arr);
  }

  return (
    <div className="intel-sections" style={{ padding: '0 16px 16px' }}>
      {layer.status === 'failed' && (
        <LayerFailureBanner
          error={layer.error}
          layerLabel="Evidence (Layer 1)"
          onRetry={regenLayer}
          pending={regenPending}
        />
      )}
      <ComplianceTable compliance={ev.compliance} />
      {Array.from(grouped.entries()).map(([cat, findings]) => (
        <div key={cat} className="intel-section is-open">
          <div className="intel-section__head" style={{ cursor: 'default' }}>
            <span className="intel-section__title">{EVIDENCE_CATEGORY_LABEL[cat]}</span>
            <span className="intel-section__flags">{findings.length}</span>
          </div>
          <div className="intel-section__body">
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', opacity: 0.6 }}>
                  <th style={{ padding: '6px 8px', width: '40%' }}>Finding</th>
                  <th style={{ padding: '6px 8px', width: '18%' }}>Type</th>
                  <th style={{ padding: '6px 8px', width: '22%' }}>Source</th>
                  <th style={{ padding: '6px 8px', width: '20%' }}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {findings.map((f) => (
                  <tr
                    key={f.id}
                    id={`ev-${f.id}`}
                    style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    <td style={{ padding: '8px' }}>{f.finding}</td>
                    <td style={{ padding: '8px' }}>
                      <span
                        className={`intel-pill intel-pill--${EVIDENCE_TYPE_CLASS[f.evidenceType]} intel-pill--xs`}
                      >
                        {EVIDENCE_TYPE_LABEL[f.evidenceType]}
                      </span>
                    </td>
                    <td style={{ padding: '8px' }}>
                      {f.source?.url ? (
                        <a
                          href={f.source.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="intel-sources__link"
                        >
                          ↗ {f.source.label}
                        </a>
                      ) : (
                        <span style={{ opacity: 0.4 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '8px' }}>
                      <ConfidenceChip confidence={f.confidence} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {grouped.size === 0 && (
        <p className="muted" style={{ fontSize: 12, padding: 12 }}>
          No evidence findings match your search.
        </p>
      )}
    </div>
  );
}

function ComplianceTable({
  compliance,
}: Readonly<{
  compliance: EvidencePayload['compliance'];
}>) {
  const FRAMEWORKS: Array<{ id: keyof EvidencePayload['compliance']; label: string }> = [
    { id: 'hipaa', label: 'HIPAA' },
    { id: 'gdpr', label: 'GDPR' },
    { id: 'ccpa', label: 'CCPA' },
    { id: 'soc2', label: 'SOC 2' },
    { id: 'iso27001', label: 'ISO 27001' },
  ];
  return (
    <div className="intel-compliance">
      <div className="intel-compliance__label">Compliance state (verified vs. claimed)</div>
      <div className="intel-compliance__grid">
        {FRAMEWORKS.map((f) => {
          const meta = COMPLIANCE_MAP[compliance[f.id]] ?? COMPLIANCE_MAP.unknown;
          return (
            <div
              key={f.id}
              className={`intel-compliance__chip intel-compliance--${meta.cls}`}
            >
              <span>{f.label}</span>
              <strong>{meta.label}</strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConfidenceChip({ confidence }: Readonly<{ confidence: Confidence }>) {
  const cls = confidence === 'high' ? 'clear' : confidence === 'medium' ? 'caution' : 'unknown';
  const label = confidence === 'high' ? 'High' : confidence === 'medium' ? 'Medium' : 'Low';
  return <span className={`intel-pill intel-pill--${cls} intel-pill--xs`}>{label}</span>;
}

// ── Threat tab ──────────────────────────────────────────────────────────────

function ThreatTab({
  layer,
  evidenceFindings,
  search,
  regenLayer,
  regenPending,
  jumpToEvidence,
}: Readonly<{
  layer: IntelLayer<ThreatPayload> | null;
  evidenceFindings: EvidenceFinding[];
  search: string;
  regenLayer: () => void;
  regenPending: boolean;
  jumpToEvidence: () => void;
}>) {
  const evById = useMemo(() => {
    const m = new Map<string, EvidenceFinding>();
    for (const f of evidenceFindings) m.set(f.id, f);
    return m;
  }, [evidenceFindings]);

  if (!layer || layer.status === 'pending' || !layer.payload) {
    if (layer?.status === 'failed') {
      return (
        <LayerFailureBanner
          error={layer.error}
          layerLabel="Threat Model (Layer 2)"
          onRetry={regenLayer}
          pending={regenPending}
        />
      );
    }
    return <EmptyLayerNote text="Threat layer hasn't run yet." onRun={regenLayer} pending={regenPending} />;
  }
  const q = search.trim().toLowerCase();
  const matches = (f: ThreatFinding): boolean =>
    !q ||
    f.riskArea.toLowerCase().includes(q) ||
    f.operationalImplication.toLowerCase().includes(q) ||
    f.controlGap.toLowerCase().includes(q);
  const findings = layer.payload.findings.filter(matches);

  return (
    <div className="intel-sections" style={{ padding: '0 16px 16px' }}>
      {layer.status === 'failed' && (
        <LayerFailureBanner
          error={layer.error}
          layerLabel="Threat Model (Layer 2)"
          onRetry={regenLayer}
          pending={regenPending}
        />
      )}
      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', opacity: 0.6 }}>
            <th style={{ padding: '6px 8px', width: '18%' }}>Risk Area</th>
            <th style={{ padding: '6px 8px', width: '28%' }}>Operational Implication</th>
            <th style={{ padding: '6px 8px', width: '22%' }}>Control Gap</th>
            <th style={{ padding: '6px 8px', width: '12%' }}>Candidate</th>
            <th style={{ padding: '6px 8px', width: '20%' }}>Evidence</th>
          </tr>
        </thead>
        <tbody>
          {findings.map((f) => (
            <tr key={f.id} style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <td style={{ padding: '8px', fontWeight: 600 }}>{f.riskArea}</td>
              <td style={{ padding: '8px' }}>{f.operationalImplication}</td>
              <td style={{ padding: '8px' }}>{f.controlGap}</td>
              <td style={{ padding: '8px' }}>
                <SeverityCandidatePill candidate={f.severityCandidate} />
              </td>
              <td style={{ padding: '8px' }}>
                {f.evidenceRefs.length === 0 && <span style={{ opacity: 0.4 }}>—</span>}
                {f.evidenceRefs.map((ref) => {
                  const ev = evById.get(ref);
                  return (
                    <button
                      key={ref}
                      type="button"
                      onClick={() => {
                        jumpToEvidence();
                        setTimeout(() => {
                          document.getElementById(`ev-${ref}`)?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                          });
                        }, 50);
                      }}
                      className="intel-sources__link"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        marginRight: 8,
                        cursor: 'pointer',
                      }}
                      title={ev?.finding ?? ref}
                    >
                      ↳ {ref}
                    </button>
                  );
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {findings.length === 0 && (
        <p className="muted" style={{ fontSize: 12, padding: 12 }}>
          No threat findings match your search.
        </p>
      )}
    </div>
  );
}

function SeverityCandidatePill({ candidate }: Readonly<{ candidate: SeverityCandidate }>) {
  const sev = candidateToSeverity(candidate);
  return (
    <span className={`intel-pill intel-pill--${sev} intel-pill--xs`}>
      {SEVERITY_CANDIDATE_LABEL[candidate]}
    </span>
  );
}

// ── Risk Summary tab ────────────────────────────────────────────────────────

function RiskTab({
  rules,
  payload,
  openSections,
  toggleSection,
  notesByKey,
  onSaveNote,
  role,
  saving,
  savingSection,
  search,
}: Readonly<{
  rules: IntelLayer<RulesPayload> | null;
  payload: VendorIntelPayload;
  openSections: Record<SectionKey, boolean>;
  toggleSection: (k: SectionKey) => void;
  notesByKey: Map<SectionKey, VendorReviewerNote>;
  onSaveNote: (section: SectionKey, text: string) => void;
  role: Role;
  saving: boolean;
  savingSection: SectionKey | undefined;
  search: string;
}>) {
  const rulesPayload = rules?.payload ?? null;

  function sectionMatches(k: SectionKey): boolean {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const label = SECTION_DEFS.find((d) => d.key === k)?.label.toLowerCase() ?? '';
    if (label.includes(q)) return true;
    return payload[k].bullets.some(
      (b) => b.label.toLowerCase().includes(q) || b.value.toLowerCase().includes(q),
    );
  }

  return (
    <>
      {rules?.status === 'failed' && (
        <LayerFailureBanner
          error={rules.error}
          layerLabel="Risk Summary (Layer 3 — rules)"
          onRetry={() => undefined}
          pending={false}
          hideRetry
        />
      )}
      {rulesPayload && <RecommendationBanner rules={rulesPayload} />}
      {rulesPayload && rulesPayload.triggeredRules.length > 0 && (
        <TriggeredRulesTable rules={rulesPayload.triggeredRules} />
      )}
      <div className="intel-sections">
        {SECTION_DEFS.filter((d) => sectionMatches(d.key)).map((d) => (
          <Section
            key={d.key}
            sectionKey={d.key}
            label={d.label}
            data={payload[d.key]}
            isOpen={!!openSections[d.key]}
            onToggle={() => toggleSection(d.key)}
            note={notesByKey.get(d.key)}
            onSaveNote={(text) => onSaveNote(d.key, text)}
            role={role}
            saving={saving && savingSection === d.key}
          />
        ))}
      </div>
    </>
  );
}

function RecommendationBanner({ rules }: Readonly<{ rules: RulesPayload }>) {
  const sev = RECOMMENDATION_SEVERITY[rules.recommendation];
  return (
    <div
      className={`intel-flags intel-flags--rec intel--${sev}`}
      style={{
        margin: '12px 16px 0',
        padding: 14,
        borderRadius: 8,
        border: `1px solid var(--color-border, rgba(255,255,255,0.15))`,
        background:
          sev === 'critical'
            ? 'rgba(255,90,90,0.10)'
            : sev === 'caution'
            ? 'rgba(255,180,80,0.10)'
            : sev === 'clear'
            ? 'rgba(80,200,120,0.10)'
            : 'rgba(180,180,180,0.10)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Procurement recommendation (deterministic)
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>
            {RECOMMENDATION_LABEL[rules.recommendation]}
          </div>
        </div>
        <SeverityPill severity={rules.overallSeverity} />
      </div>
    </div>
  );
}

function TriggeredRulesTable({ rules }: Readonly<{ rules: TriggeredRule[] }>) {
  return (
    <div className="intel-section is-open" style={{ margin: '12px 16px 0' }}>
      <div className="intel-section__head" style={{ cursor: 'default' }}>
        <span className="intel-section__title">Triggered rules</span>
        <span className="intel-section__flags">{rules.length}</span>
      </div>
      <div className="intel-section__body">
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', opacity: 0.6 }}>
              <th style={{ padding: '6px 8px', width: '30%' }}>Rule</th>
              <th style={{ padding: '6px 8px', width: '14%' }}>Severity</th>
              <th style={{ padding: '6px 8px' }}>Basis</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: 11 }}>{r.rule}</td>
                <td style={{ padding: '8px' }}>
                  <SeverityPill severity={r.severity} size="xs" />
                </td>
                <td style={{ padding: '8px' }}>{r.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Shared subcomponents ───────────────────────────────────────────────────

function LayerFailureBanner({
  error,
  layerLabel,
  onRetry,
  pending,
  hideRetry,
}: Readonly<{
  error: string | null;
  layerLabel: string;
  onRetry: () => void;
  pending: boolean;
  hideRetry?: boolean;
}>) {
  return (
    <div
      className="intel-flags"
      style={{
        background: 'rgba(255,90,90,0.08)',
        borderColor: 'rgba(255,90,90,0.35)',
        margin: '12px 16px',
      }}
    >
      <div className="intel-flags__title">
        <span className="intel-flags__bullet" /> {layerLabel} failed
      </div>
      <ul className="intel-flags__list">
        <li>
          <span>
            The agent didn't return valid output. {hideRetry ? 'Will re-run when upstream layers succeed.' : 'Retry this layer to try again.'}
          </span>
          {error && (
            <span style={{ marginLeft: 8, opacity: 0.7, fontSize: '0.85em' }}>
              ({error})
            </span>
          )}
        </li>
      </ul>
      {!hideRetry && (
        <button
          className="btn btn--secondary btn--sm"
          disabled={pending}
          onClick={onRetry}
          style={{ marginTop: 8 }}
        >
          {pending ? 'Retrying…' : '↻ Retry this layer'}
        </button>
      )}
    </div>
  );
}

function EmptyLayerNote({
  text,
  onRun,
  pending,
}: Readonly<{ text: string; onRun: () => void; pending: boolean }>) {
  return (
    <div style={{ padding: 24, textAlign: 'center', opacity: 0.7, fontSize: 13 }}>
      <p>{text}</p>
      <button
        className="btn btn--primary btn--sm"
        disabled={pending}
        onClick={onRun}
        style={{ marginTop: 8 }}
      >
        {pending ? 'Researching…' : 'Run this layer'}
      </button>
    </div>
  );
}

function Section({
  sectionKey,
  label,
  data,
  isOpen,
  onToggle,
  note,
  onSaveNote,
  role,
  saving,
}: Readonly<{
  sectionKey: SectionKey;
  label: string;
  data: IntelSection;
  isOpen: boolean;
  onToggle: () => void;
  note: VendorReviewerNote | undefined;
  onSaveNote: (text: string) => void;
  role: Role;
  saving: boolean;
}>) {
  const flagCount = data.bullets.filter(
    (b) => b.severity === 'critical' || b.severity === 'caution',
  ).length;
  const canEdit = role === 'it_reviewer' || role === 'legal_reviewer' || role === 'admin';
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? note?.note ?? '';

  function save(): void {
    if (draft === null) return;
    const trimmed = draft.trim();
    if (trimmed === (note?.note ?? '')) {
      setDraft(null);
      return;
    }
    onSaveNote(trimmed);
    setDraft(null);
  }

  return (
    <div className={`intel-section ${isOpen ? 'is-open' : ''}`}>
      <button
        type="button"
        className="intel-section__head"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className={`intel-section__chevron ${isOpen ? 'is-open' : ''}`}>›</span>
        <span className="intel-section__title">{label}</span>
        <SeverityPill severity={data.status} size="sm" />
        {flagCount > 0 && (
          <span className="intel-section__flags">
            {flagCount} flag{flagCount === 1 ? '' : 's'}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="intel-section__body">
          {data.bullets.length === 0 && (
            <p className="muted" style={{ fontSize: 12 }}>No data.</p>
          )}
          {data.bullets.length > 0 && (
            <dl className="intel-dl">
              {data.bullets.map((b, i) => (
                <BulletRow key={i} bullet={b} />
              ))}
            </dl>
          )}
          {sectionKey === 'legal' && data.compliance && (
            <LegacyComplianceMatrix compliance={data.compliance} />
          )}
          {data.sources && data.sources.length > 0 && (
            <div className="intel-sources">
              <span className="intel-sources__label">Sources</span>
              {data.sources.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noreferrer" className="intel-sources__link">
                  ↗ {s.label}
                </a>
              ))}
            </div>
          )}
          <div className="intel-note">
            <label className="intel-note__label">
              Reviewer note (per-vendor, shared across IT &amp; Legal)
            </label>
            <textarea
              value={value}
              disabled={!canEdit || saving}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={save}
              placeholder={
                canEdit
                  ? `Add a note for the ${label.toLowerCase()} section. Visible to other reviewers and on the audit log.`
                  : 'Only IT, Legal, and Admin can edit notes.'
              }
            />
            {note && (
              <p style={{ fontSize: 10, color: 'var(--color-steel)', marginTop: 4 }}>
                Last updated by {note.updatedByName} · {formatRelative(note.updatedAt)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BulletRow({
  bullet,
}: Readonly<{
  bullet: { label: string; value: string; severity?: Severity; source?: { label: string; url: string } };
}>) {
  return (
    <>
      <dt>{bullet.label}</dt>
      <dd>
        <span>{bullet.value}</span>
        {bullet.severity && bullet.severity !== 'clear' && bullet.severity !== 'unknown' && (
          <SeverityPill severity={bullet.severity} size="xs" />
        )}
        {bullet.source?.url && (
          <a
            href={bullet.source.url}
            target="_blank"
            rel="noreferrer noopener"
            className="intel-sources__link"
            style={{ marginLeft: 8 }}
            title={bullet.source.url}
          >
            ↗ {bullet.source.label}
          </a>
        )}
      </dd>
    </>
  );
}

function LegacyComplianceMatrix({
  compliance,
}: Readonly<{ compliance: Record<'hipaa' | 'gdpr' | 'ccpa' | 'soc2', ComplianceStatus> }>) {
  const FRAMEWORKS: Array<{ id: 'hipaa' | 'gdpr' | 'ccpa' | 'soc2'; label: string }> = [
    { id: 'hipaa', label: 'HIPAA' },
    { id: 'gdpr', label: 'GDPR' },
    { id: 'ccpa', label: 'CCPA' },
    { id: 'soc2', label: 'SOC 2' },
  ];
  return (
    <div className="intel-compliance">
      <div className="intel-compliance__label">Compliance by framework</div>
      <div className="intel-compliance__grid">
        {FRAMEWORKS.map((f) => {
          const meta = COMPLIANCE_MAP[compliance[f.id]] ?? COMPLIANCE_MAP.unknown;
          return (
            <div
              key={f.id}
              className={`intel-compliance__chip intel-compliance--${meta.cls}`}
            >
              <span>{f.label}</span>
              <strong>{meta.label}</strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SeverityPill({
  severity,
  size,
}: Readonly<{ severity: Severity; size?: 'sm' | 'xs' }>) {
  const sizeCls = size === 'sm' ? 'intel-pill--sm' : size === 'xs' ? 'intel-pill--xs' : '';
  return (
    <span className={`intel-pill intel-pill--${severity} ${sizeCls}`}>
      {SEVERITY_LABEL[severity]}
    </span>
  );
}

function GemIcon(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2 L2 7 L12 12 L22 7 Z" />
      <path d="M2 17 L12 22 L22 17" />
      <path d="M2 12 L12 17 L22 12" />
    </svg>
  );
}
